import React, {useContext} from 'react'
import {useSelector} from 'react-redux'
import {HotelCardContext} from 'components/HotelCardContext'
import {HotelCardUnavailability} from 'components/HotelCardUnavailability'
import {HistoricalPriceOffer} from 'components/Offer/HistoricalPriceOffer'
import {PrimaryOffer} from 'components/Offer/Primary'
import {useDatelessSeoSRP} from 'hooks/useDatelessSeoSRP'
import {useOfferContainer} from 'hooks/useOfferContainer'
import {useOtaMode} from 'hooks/useOtaMode'
import {getAnalyticsPageName} from 'modules/meta/selectors'
import {
  getAnchorComplete,
  getHotel,
  getHotelHasOffers,
  getOfferAccessFlowSettings,
  getOfferEntity,
  getSplitBookingDetails
} from 'modules/sapiSearch/selectors'

import {Box} from '@vio/atlas/helpers/Box'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {OfferPromoKey} from '@vio/core/src/offer/types/offer'

import {useTopOfferDelay} from '../../Offer/hooks/useTopOfferDelay'
import {TopOfferInfoSkeleton} from './Skeleton'

const SOURCE_COMPONENT = 'DesktopTopOffers'

export const TopOffer: React.FC = () => {
  const isDatelessSeoSRP = useDatelessSeoSRP()
  const {hotelId, isAnchor} = useContext(HotelCardContext)

  const {
    monetisation: {cashback}
  } = useBrandCapabilities()
  const hotelHasOffers = useSelector(state => getHotelHasOffers(state, hotelId))
  const page = useSelector(getAnalyticsPageName)

  const isAnchorComplete = useSelector(getAnchorComplete)
  const hotel = useSelector(state => getHotel(state, hotelId))
  const offerEntity = useSelector(state => getOfferEntity(state, hotelId))
  const web2appPromo = offerEntity?.promos?.[OfferPromoKey.Web2App]
  const {isOTAMode} = useOtaMode()

  const {anchorPrice, anchorPriceTotal, primaryOffer, offerVariant} =
    useOfferContainer({
      hotelId,
      offerEntity,
      withGreatDealTag: true
    })

  const {isAppLocked: hasWeb2AppPromo} = useSelector(state =>
    getOfferAccessFlowSettings(state, hotelId, web2appPromo?.id)
  )

  const {
    showSplitBookingLoader,
    splitBookingOffers,
    splitBookingBundle,
    hasPrimarySplitBookingOffer
  } = useSelector(state => getSplitBookingDetails(state, hotelId))

  const isOffersUnavailable = isAnchorComplete && !hotelHasOffers && isAnchor

  const showAnchorOfferLoader = useTopOfferDelay({isAnchor, page})

  if (isDatelessSeoSRP) {
    return (
      <HistoricalPriceOffer
        hotelId={hotel?.objectID}
        price={hotel?.pricing?.minRateInUserCurrency}
      />
    )
  }

  if (
    isOffersUnavailable &&
    !showSplitBookingLoader &&
    !hasPrimarySplitBookingOffer &&
    !showAnchorOfferLoader
  ) {
    return (
      <HotelCardUnavailability sourceComponent="HotelCardDesktopAvailabilityPane" />
    )
  }

  if (hasPrimarySplitBookingOffer && !showSplitBookingLoader) {
    return (
      <PrimaryOffer
        isSplitBooking
        anchorPrice={anchorPrice}
        anchorPriceTotal={anchorPriceTotal}
        splitBookingOffers={splitBookingOffers}
        splitBookingBundle={splitBookingBundle}
        shouldShowTaxationAndNightly
        variant="PrimaryDefault"
        hotelId={hotelId}
        showPriceIndication
        sourceComponent={SOURCE_COMPONENT}
        showCashback={cashback.enabled}
        showProviderLogo={!isOTAMode}
      />
    )
  }

  // Show loader
  if (
    !hotel ||
    !hotelHasOffers ||
    (showSplitBookingLoader && isAnchor) ||
    showAnchorOfferLoader
  )
    return <TopOfferInfoSkeleton />

  return (
    <Box display="flex" flexDirection="column" justifyContent="stretch">
      <Box
        height="100%"
        display="flex"
        flexDirection="row"
        justifyContent="stretch"
      >
        <PrimaryOffer
          hasWeb2AppPromo={hasWeb2AppPromo}
          anchorPrice={anchorPrice}
          anchorPriceTotal={anchorPriceTotal}
          hotelId={hotelId}
          offer={primaryOffer}
          offerVariant={offerVariant}
          variant="PrimaryDefault"
          showPriceIndication
          shouldShowTaxationAndNightly
          hasInteraction
          sourceComponent={SOURCE_COMPONENT}
          showCashback={cashback.enabled}
          showProviderLogo={!isOTAMode}
        />
      </Box>
    </Box>
  )
}
